import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import ComicCard from '@/components/base/ComicCard';
import {
  categories,
  getCategoryNameBySlug,
} from '@/mocks/categories';
import {
  getLatestUpdates,
  getPopularComics,
  getTopRated,
  getComicsByCategory,
} from '@/mocks/comics';

const HomePage = () => {
  const latestUpdates = getLatestUpdates(12);
  const popularComics = getPopularComics(6);
  const topRated = getTopRated(6);
  const romanceComics = getComicsByCategory('lianai-manhua').slice(0, 6);
  const fantasyComics = getComicsByCategory('qihuan-manhua').slice(0, 6);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '汗汗漫画 - 韩国漫画免费在线观看 | 好看的韩国漫画大全',
    description:
      '汗汗漫画平台支持免费在线观看韩国漫画大全，流畅的下拉式阅读体验带来视觉享受，每日更新帮助用户轻松追更热门韩漫。',
    url: 'https://hanhanmanhua.com/',
    isPartOf: {
      '@type': 'WebSite',
      name: '汗汗漫画',
      url: 'https://hanhanmanhua.com/',
    },
    about: {
      '@type': 'Organization',
      name: '汗汗漫画',
      url: 'https://hanhanmanhua.com/',
      description: '专注于韩国漫画免费在线阅读的中文平台，提供流畅的下拉式阅读体验。',
      sameAs: ['https://hanhanmanhua.com/'],
    },
  };

  return (
    <>
      <SEOHead
        title="汗汗漫画 - 韩国漫画免费在线观看 | 好看的韩国漫画大全 | 下拉式漫画阅读"
        description="汗汗漫画平台支持免费在线观看韩国漫画大全，提供流畅的下拉式阅读体验。每日更新热门韩漫，涵盖恋爱、奇幻、动作、悬疑、搞笑等多种类型，为用户带来视觉享受，帮助用户轻松追更热门韩漫。"
        keywords="汗汗漫画,韩国漫画,韩漫,免费在线观看,漫画大全,下拉式阅读,热门韩漫"
        canonicalUrl="https://hanhanmanhua.com/"
        structuredData={structuredData}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <header className="relative w-full h-[520px] md:h-[620px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20comic%20book%20illustration%20background%20with%20soft%20warm%20orange%20and%20cream%20gradients%2C%20stylized%20manga%20panel%20lines%2C%20minimal%20aesthetic%2C%20clean%20composition%2C%20modern%20webtoon%20inspired%20design%2C%20no%20text%2C%20atmospheric%20lighting&width=1600&height=700&seq=hero-bg-hanman&orientation=landscape"
            alt="汗汗漫画韩国漫画阅读平台"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
          <div className="relative z-10 flex flex-col items-center justify-center h-full w-full px-4 md:px-6 text-center">
            <h1 className="font-heading text-3xl md:text-5xl text-background-50 mb-4 max-w-2xl leading-tight">
              韩国漫画免费在线观看
            </h1>
            <p className="text-base md:text-lg text-background-50/85 max-w-xl mb-8 leading-relaxed">
              流畅的下拉式阅读体验，每日更新热门韩漫<br />
              恋爱、奇幻、动作、悬疑……好看的韩国漫画都在这里
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/category/remen-tuijian"
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
              >
                开始阅读
              </Link>
              <Link
                to="/category/lianai-manhua"
                className="px-8 py-3 bg-background-50/20 backdrop-blur-sm text-background-50 border border-background-50/30 rounded-full font-medium text-sm hover:bg-background-50/30 transition-all duration-300 whitespace-nowrap"
              >
                热门排行
              </Link>
            </div>
          </div>
        </header>

        <main>
          {/* Category Navigation */}
          <section className="py-8 bg-background-50 border-b border-secondary-200/40">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-3 overflow-x-auto pb-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-1.5 px-4 py-2 bg-secondary-100 hover:bg-primary-100 rounded-full text-sm text-foreground-700 hover:text-primary-700 whitespace-nowrap transition-all duration-200 flex-shrink-0"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-xs`}></i>
                    </span>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Latest Updates */}
          <section className="py-12 md:py-16 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-heading text-2xl text-foreground-950">
                  最新更新
                </h2>
                <Link
                  to="/category/remen-tuijian"
                  className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  查看全部
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </span>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {latestUpdates.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </div>
          </section>

          {/* Popular Comics */}
          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="font-heading text-2xl text-foreground-950">
                    热门漫画
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">最多人观看的韩国漫画</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {popularComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </div>
          </section>

          {/* Romance Section */}
          <section className="py-12 md:py-16 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="font-heading text-2xl text-foreground-950">
                    恋爱漫画
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">甜蜜浪漫的韩国恋爱漫画</p>
                </div>
                <Link
                  to="/category/lianai-manhua"
                  className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  更多恋爱漫画
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </span>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {romanceComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </div>
          </section>

          {/* Fantasy Section */}
          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="font-heading text-2xl text-foreground-950">
                    奇幻漫画
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">魔幻冒险的韩国奇幻漫画</p>
                </div>
                <Link
                  to="/category/qihuan-manhua"
                  className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  更多奇幻漫画
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </span>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {fantasyComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </div>
          </section>

          {/* Top Rated */}
          <section className="py-12 md:py-16 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="font-heading text-2xl text-foreground-950">
                    高分推荐
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">评分最高的韩国漫画精选</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {topRated.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </div>
          </section>

          {/* Platform Features */}
          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl text-foreground-950 text-center mb-10">
                为什么选择汗汗漫画
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: 'ri-smartphone-line', title: '下拉式阅读', desc: '流畅的竖向滚动阅读体验，适配各种设备，随时随地畅享韩国漫画' },
                  { icon: 'ri-refresh-line', title: '每日更新', desc: '热门韩漫每日准时更新，不再错过任何精彩剧情，轻松追更无压力' },
                  { icon: 'ri-bookmark-line', title: '种类丰富', desc: '涵盖恋爱、奇幻、动作、悬疑、搞笑等多种类型，满足各种阅读口味' },
                  { icon: 'ri-shield-check-line', title: '高清画质', desc: '全部漫画采用高清画质呈现，细腻的画面表现力带来极致视觉享受' },
                ].map((feature, i) => (
                  <div key={i} className="text-center p-6">
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className={`${feature.icon} text-2xl`}></i>
                    </div>
                    <h3 className="font-heading text-base text-foreground-950 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      {feature.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Banner */}
          <section className="relative w-full py-20 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20orange%20and%20cream%20abstract%20gradient%20background%20with%20subtle%20comic%20panel%20lines%2C%20minimal%20clean%20aesthetic%2C%20soft%20atmospheric%20lighting%2C%20modern%20webtoon%20inspired%20design%2C%20no%20text%2C%20inviting%20warmth&width=1600&height=500&seq=cta-banner-hanman&orientation=landscape"
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
            <div className="relative z-10 text-center px-4 md:px-6">
              <h2 className="font-heading text-2xl md:text-3xl text-background-50 mb-4">
                即刻开启你的韩漫之旅
              </h2>
              <p className="text-base text-background-50/80 mb-8 max-w-lg mx-auto">
                数千部韩国漫画免费阅读，每日更新，流畅体验，尽在汗汗漫画
              </p>
              <Link
                to="/category/remen-tuijian"
                className="inline-flex px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
              >
                立即探索
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default HomePage;